import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { BlogCard } from '../components/BlogCard';
import { API } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';

const Blog = () => {
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(res => { setPosts(res.data.posts); setCategories(res.data.categories); })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = active ? posts.filter(p => p.category === active) : posts;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Blog - Paper & Construction Industry Insights"
        description="Practical guides on paper chemicals, dyes, OBA, AKD sizing, retention aids, paper machine showers and construction chemicals from VB Global, Coimbatore."
        keywords="paper industry blog, paper chemicals guide, paper dyes guide, OBA whiteness, AKD sizing tips, retention aid, paper machine shower maintenance, fly ash brick admixture, VB Global blog"
      />
      <section className="relative py-20 bg-gradient-to-br from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 text-[#F5E6B8] px-4 py-2 rounded-full mb-6 backdrop-blur-sm border border-[#D4AF37]/40">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium">{t('nav.blog')}</span>
            </div>
            <h1 data-testid="blog-hero-title" className="text-4xl md:text-5xl font-bold mb-6">{t('blog.heroTitle')}</h1>
            <p className="text-xl text-gray-200">{t('blog.heroSub')}</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filters">
            {['', ...categories].map(cat => (
              <button
                key={cat || 'all'}
                data-testid={`blog-filter-${cat ? cat.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'all'}`}
                onClick={() => setActive(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors ${
                  active === cat ? 'bg-[#8B2C3A] border-[#8B2C3A] text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-[#8B2C3A] hover:text-[#8B2C3A]'
                }`}
              >
                {cat || t('blog.all')}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500 gap-2" data-testid="blog-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('blog.loading')}
            </div>
          ) : visible.length === 0 ? (
            <p className="text-center text-gray-500 py-20" data-testid="blog-empty">{t('blog.noPosts')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {visible.map((post, i) => <BlogCard key={post.id} post={post} index={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
